import Icon from '../ui/Icon';
import FieldError from '../ui/FieldError';
import { formatDateTime, formatEuro, timeAgo } from '../../utils/format';
import { getFuelMeta, sizeLabel } from './fuelTypes';
import { computeSavings, isDirty } from './priceForm';

function NumberField({ id, label, value, onChange, error, prefix, suffix, accent = false, disabled }) {
  const tone = accent
    ? 'border-secondary/30 text-secondary font-semibold focus:border-secondary focus:ring-secondary'
    : 'border-outline-variant/60 text-on-surface focus:border-primary-container focus:ring-primary-container';
  const invalid = 'border-error bg-status-alert-bg/40 text-on-surface focus:border-error focus:ring-error';

  return (
    <div className="min-w-0">
      <label
        htmlFor={id}
        className={`block text-[11px] leading-[14px] font-medium mb-1.5 ${accent ? 'text-secondary' : 'text-on-surface-variant'}`}
      >
        {label}
      </label>
      <div className="relative flex items-center">
        {prefix && (
          <span
            className={`absolute left-3 font-data-mono text-xs pointer-events-none select-none ${accent ? 'text-secondary font-semibold' : 'text-on-surface-variant font-medium'}`}
          >
            {prefix}
          </span>
        )}
        <input
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          className={`w-full h-10 ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? (suffix.length > 1 ? 'pr-11' : 'pr-8') : 'pr-3'} bg-surface-container-lowest border rounded-lg font-data-mono text-xs shadow-sm outline-none transition-all focus:ring-1 disabled:opacity-60 ${error ? invalid : tone}`}
        />
        {suffix && (
          <span className="absolute right-3 text-on-surface-variant font-data-mono text-xs font-medium pointer-events-none select-none">
            {suffix}
          </span>
        )}
      </div>
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
}

/**
 * One fuel benchmark row. Stacks vertically in narrow cards and switches to the
 * single-row layout from the design once the card is wide enough (container query).
 */
export default function FuelPriceCard({ price, draft, errors, saving, serverError, onChange, onReset, onUpdate }) {
  const meta = getFuelMeta(price.fuelType);
  const { unit } = price;
  const dirty = isDirty(draft, price);
  const savings = computeSavings(draft);
  const idPrefix = `fuel-${price.id}`;

  return (
    <article
      className={`@container bg-surface-container-lowest rounded-xl p-5 border shadow-xs hover:shadow-sm transition-shadow ${
        dirty ? 'border-status-pending-border' : 'border-outline-variant/30'
      }`}
    >
      {serverError && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-2 rounded-lg border border-status-alert-border bg-status-alert-bg px-3 py-2 text-xs text-status-alert-text"
        >
          <Icon name="error" size={16} />
          <span>{serverError}</span>
        </div>
      )}

      <div className="flex flex-col @[52rem]:flex-row @[52rem]:items-start justify-between gap-4">
        <div className="flex items-center gap-3 @[52rem]:w-[250px] @[52rem]:pt-4 shrink-0">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${meta.iconClass}`}>
            <Icon name={meta.icon} size={26} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <h3 className="text-base font-bold text-on-surface">{meta.name}</h3>
              {meta.grade && (
                <span className="text-[11px] font-semibold bg-surface-container text-on-surface-variant px-1.5 py-0.5 rounded">
                  {meta.grade}
                </span>
              )}
            </div>
            <p className="text-xs text-on-surface-variant">{meta.description}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1 min-w-0">
          <NumberField
            id={`${idPrefix}-station`}
            label={`Station Price (€/${unit})`}
            prefix="€"
            value={draft.stationPrice}
            error={errors.stationPrice}
            disabled={saving}
            onChange={(v) => onChange('stationPrice', v)}
          />
          <NumberField
            id={`${idPrefix}-pompi`}
            label={`Pompi Price (€/${unit})`}
            prefix="€"
            accent
            value={draft.pompiPrice}
            error={errors.pompiPrice}
            disabled={saving}
            onChange={(v) => onChange('pompiPrice', v)}
          />
          <NumberField
            id={`${idPrefix}-size`}
            label={`${sizeLabel(unit)} (${unit})`}
            suffix={unit}
            value={draft.sizePerFillUp}
            error={errors.sizePerFillUp}
            disabled={saving}
            onChange={(v) => onChange('sizePerFillUp', v)}
          />
        </div>

        <div className="flex flex-row @[52rem]:flex-col items-center @[52rem]:items-end justify-between gap-2 shrink-0 @[52rem]:pt-1">
          <div className="text-left @[52rem]:text-right">
            {savings && savings.diff > 0 ? (
              <div className="inline-flex items-center gap-1 text-[13px] font-semibold text-secondary tabular">
                <Icon name="arrow_downward" size={16} />
                {formatEuro(savings.diff)}/{unit} diff • {formatEuro(savings.perFillUp)} saved
              </div>
            ) : (
              <div className="inline-flex items-center gap-1 text-[13px] font-semibold text-error">
                <Icon name="warning" size={16} />
                {savings ? 'No saving' : 'Enter valid values'}
              </div>
            )}
            <div className="text-[11px] font-semibold text-on-surface-variant" title={formatDateTime(price.updatedAt)}>
              {dirty ? (
                <span className="text-status-pending-text">Unsaved changes</span>
              ) : (
                <>Last updated: {timeAgo(price.updatedAt)}</>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onReset}
              disabled={!dirty || saving}
              title="Discard changes and restore the saved values"
              className="h-8 px-3 rounded-lg bg-surface-container hover:bg-surface-container-high text-on-surface text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={onUpdate}
              disabled={!dirty || saving}
              className="h-8 px-3 min-w-[76px] rounded-lg bg-primary-container hover:bg-primary text-on-primary text-xs font-semibold shadow-sm transition-all inline-flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Icon name="progress_activity" size={14} className="animate-spin" />
                  Saving
                </>
              ) : (
                'Update'
              )}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
